/* CaptionLengthPicker — small, reusable UI to set a target caption length
 * (in words) on AI-generation forms. Renders quick presets + manual number input.
 *
 * Three modes the parent reads from the value:
 *   - { mode: 'auto' }            → caption stays short / platform-neutral
 *   - { mode: 'fixed', words: N } → all posts target ~N words (engagement hook)
 *   - { mode: 'per_platform' }    → LinkedIn ~200, Instagram ~100, X/Bluesky auto, etc.
 *
 * Props:
 *   value: { mode: 'auto' | 'fixed' | 'per_platform', words?: number }
 *   onChange(next): void
 *   className?: string
 */
import { Button } from './ui/button';
import { Input } from './ui/input';

const FIXED_PRESETS = [
  { id: 'short',  label: 'Short',  value: 40,  hint: '~40 words · X / TikTok' },
  { id: 'medium', label: 'Medium', value: 100, hint: '~100 words · IG / FB' },
  { id: 'long',   label: 'Long',   value: 200, hint: '~200 words · LinkedIn' },
];

// Tiny preview rendered when the user picks "Per platform" so they understand
// what they just opted into. Keep aligned with backend's PLATFORM_DEFAULT_TARGET_WORDS.
const PER_PLATFORM_PREVIEW = [
  { label: 'LinkedIn',  hint: '~200 words' },
  { label: 'Instagram', hint: '~100 words' },
  { label: 'Facebook',  hint: '~100 words' },
  { label: 'X / Bluesky', hint: 'Auto (short)' },
  { label: 'YouTube Shorts / TikTok', hint: 'Auto (short)' },
];

const normalize = (v) => {
  if (!v) return { mode: 'auto' };
  if (typeof v === 'number') return { mode: 'fixed', words: v };
  if (v.mode) return v;
  return { mode: 'auto' };
};

export const CaptionLengthPicker = ({ value, onChange, className = '' }) => {
  const state = normalize(value);
  const setAuto = () => onChange({ mode: 'auto' });
  const setFixed = (words) => onChange({ mode: 'fixed', words });
  const setPerPlatform = () => onChange({ mode: 'per_platform' });

  const isAuto = state.mode === 'auto';
  const isPerPlatform = state.mode === 'per_platform';
  const fixedWords = state.mode === 'fixed' ? state.words : null;

  const summary =
    isPerPlatform ? 'Per-platform smart defaults'
      : isAuto ? 'Auto'
      : `~${fixedWords} words`;

  return (
    <div className={`space-y-2 ${className}`} data-testid="caption-length-picker">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-zinc-700">Caption length</label>
        <span className="text-xs text-zinc-500">{summary}</span>
      </div>

      {/* Row 1: Auto / Short / Medium / Long */}
      <div className="grid grid-cols-4 gap-1.5">
        <Button
          type="button"
          variant={isAuto ? 'default' : 'outline'}
          size="sm"
          onClick={setAuto}
          className={`h-auto py-1.5 px-2 text-xs ${isAuto ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
          data-testid="caption-length-auto"
        >
          Auto
        </Button>
        {FIXED_PRESETS.map((p) => {
          const active = fixedWords === p.value;
          return (
            <Button
              key={p.id}
              type="button"
              variant={active ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFixed(p.value)}
              className={`h-auto py-1.5 px-2 text-xs ${active ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
              data-testid={`caption-length-${p.id}`}
            >
              {p.label}
            </Button>
          );
        })}
      </div>

      {/* Row 2: Per-platform smart defaults — full-width toggle */}
      <Button
        type="button"
        variant={isPerPlatform ? 'default' : 'outline'}
        size="sm"
        onClick={setPerPlatform}
        className={`w-full h-auto py-1.5 text-xs ${isPerPlatform ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
        data-testid="caption-length-per-platform"
      >
        ✨ Per-platform smart defaults
      </Button>

      {/* Custom input — only shown when in fixed mode */}
      {!isPerPlatform && (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={20}
            max={500}
            step={10}
            value={fixedWords ?? ''}
            placeholder="Custom (20-500 words)"
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') { setAuto(); return; }
              const n = Number.parseInt(raw, 10);
              if (Number.isFinite(n)) setFixed(Math.max(20, Math.min(500, n)));
            }}
            className="h-8 text-xs"
            data-testid="caption-length-custom"
          />
          {fixedWords !== null && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={setAuto}
              className="text-xs text-zinc-500"
            >
              Clear
            </Button>
          )}
        </div>
      )}

      {/* Per-platform preview chips */}
      {isPerPlatform && (
        <div className="rounded-md border border-indigo-100 bg-indigo-50/40 p-2 space-y-1" data-testid="per-platform-preview">
          {PER_PLATFORM_PREVIEW.map((row) => (
            <div key={row.label} className="flex items-center justify-between text-[11px]">
              <span className="text-zinc-700">{row.label}</span>
              <span className="text-indigo-700 font-medium">{row.hint}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-zinc-500 leading-snug">
        {isPerPlatform
          ? 'Each platform gets its best caption length — LinkedIn/Instagram-style long-form with a question + CTA, X/Bluesky/Shorts stay punchy.'
          : 'Longer captions get a question + call-to-action automatically — great for LinkedIn and Instagram engagement.'}
      </p>
    </div>
  );
};

export default CaptionLengthPicker;
